// src/lib/resume-journee.js — « EN RÉSUMÉ » (téléphone orchestrateur, GO
// Dimitri 22/08). La journée des agents ÉCRITE EN TOUTES LETTRES, lue depuis
// LES BLOCS DE LA FRISE (construireBlocs — jamais un nouveau calcul).
//
// LES RÈGLES (validées) :
//   · une phrase par agent : prénom, compte, plage en clair, état ;
//   · les manques NOMMÉS : ménage sans agent, ménage non validé ;
//   · qui est libre ;
//   · quand tout va bien, LE DIRE : « Tout est couvert aujourd'hui. » ;
//   · JAMAIS de jugement sur le travail de quelqu'un — des faits (« deux se
//     chevauchent, à démêler » oui ; « Karim est en retard » non) ;
//   · une donnée absente est OMISE, jamais inventée (pas d'heures → phrase
//     sans heures).
//
// Ton : un collègue qui résume la journée à voix haute. Pas un rapport.

import { SANS_AGENT, formaterHeureCourte } from './menage-frise'

const NOMBRES = ['aucun', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six']
function enLettres(n) {
  return n >= 0 && n < NOMBRES.length ? NOMBRES[n] : String(n)
}

// La plage d'un groupe de blocs : du premier début à la dernière fin. Une
// borne non mesurable → pas de plage du tout (jamais une heure inventée).
function plageDe(blocs) {
  let debut = Infinity
  let fin = -Infinity
  for (const b of blocs) {
    if (Number.isFinite(b.debut)) debut = Math.min(debut, b.debut)
    if (Number.isFinite(b.fin)) fin = Math.max(fin, b.fin)
  }
  if (!Number.isFinite(debut) || !Number.isFinite(fin) || fin <= debut) return null
  return `de ${formaterHeureCourte(debut)} à ${formaterHeureCourte(fin)}`
}

// La phrase d'UN agent. `moi` bascule sur « Vous ».
export function phraseAgent({ nom, moi, blocs, nbConflit }) {
  const n = blocs.length
  const plage = plageDe(blocs)
  const termines = blocs.filter((b) => b.termine).length
  const sujet = moi ? 'Vous' : nom

  let corps
  if (n === 1) {
    corps = `${sujet} ${moi ? 'avez' : 'a'} un ménage`
  } else if (n >= 3) {
    corps = `${sujet} enchaîne${moi ? 'z' : ''} ${enLettres(n)} ménages`
  } else {
    corps = `${sujet} en ${moi ? 'avez' : 'a'} ${enLettres(n)}`
  }
  if (plage) corps += `, ${plage}`

  // L'état — un FAIT, jamais un jugement.
  if (nbConflit >= 2) {
    corps += ` — ${enLettres(nbConflit)} se chevauchent, à démêler`
  } else if (termines === n && n > 0) {
    corps += n === 1 ? ', terminé' : ', tous terminés'
  } else if (termines > 0) {
    corps += `, ${enLettres(termines)} terminé${termines > 1 ? 's' : ''}`
  }
  return corps + '.'
}

// LE RÉSUMÉ COMPLET → tableau de phrases (une par ligne à l'écran).
//   blocs            — construireBlocs du jour (LA frise, pas un recalcul)
//   conflitsParAgent — Map email → blocs en conflit (detecterConflitsParAgent)
//   agentsPool       — emails du pool (pour nommer qui est libre)
//   self             — email de l'orchestrateur (phrases en « Vous »)
//   nomAgent(email)  — prénom affichable (pool_prenoms, repli dérivation)
//   nowMin           — minutes du moment (non-validé = fin passée, non terminé)
//   estAujourdhui    — les mots « aujourd'hui » et « libre » ne valent que là
export function resumeJournee({ blocs, conflitsParAgent, agentsPool = [], self, nomAgent, nowMin, estAujourdhui = true }) {
  const liste = Array.isArray(blocs) ? blocs : []
  const phrases = []
  const manques = []

  // Groupement par agent — les sans-agent à part.
  const parAgent = new Map()
  const sansAgent = []
  for (const b of liste) {
    if (!b.agent || b.agent === SANS_AGENT) { sansAgent.push(b); continue }
    if (!parAgent.has(b.agent)) parAgent.set(b.agent, [])
    parAgent.get(b.agent).push(b)
  }

  if (liste.length === 0) {
    return [estAujourdhui ? "Rien de prévu aujourd'hui." : 'Rien de prévu ce jour-là.']
  }

  // Une phrase par agent — les plus chargés d'abord, « Vous » en dernier.
  const entrees = [...parAgent.entries()].sort(([ea, a], [eb, b]) => {
    if (ea === self) return 1
    if (eb === self) return -1
    return b.length - a.length
  })
  for (const [email, blocsAgent] of entrees) {
    const enConflit = conflitsParAgent && conflitsParAgent.get ? conflitsParAgent.get(email) : null
    phrases.push(phraseAgent({
      nom: (nomAgent && nomAgent(email)) || email,
      moi: email === self,
      blocs: blocsAgent,
      nbConflit: enConflit ? enConflit.length : 0,
    }))
    if (enConflit && enConflit.length >= 2) manques.push('conflit')
  }

  // Les manques — nommés, factuels.
  for (const b of sansAgent) {
    let ph = `Un ménage n'a personne : ${b.nom || 'logement inconnu'}`
    if (Number.isFinite(b.fin)) ph += `, à caser avant ${formaterHeureCourte(b.fin)}`
    phrases.push(ph + '.')
    manques.push('sans-agent')
  }
  if (estAujourdhui && Number.isFinite(nowMin)) {
    const nonValides = liste.filter((b) =>
      b.agent && b.agent !== SANS_AGENT && !b.termine && Number.isFinite(b.fin) && b.fin < nowMin)
    for (const b of nonValides.slice(0, 2)) {
      phrases.push(`Le ménage de ${b.nom || 'logement inconnu'} n'est pas validé.`)
      manques.push('non-valide')
    }
    if (nonValides.length > 2) {
      phrases.push(`Et ${enLettres(nonValides.length - 2)} autre${nonValides.length - 2 > 1 ? 's' : ''} ménage${nonValides.length - 2 > 1 ? 's' : ''} non validé${nonValides.length - 2 > 1 ? 's' : ''}.`)
    }
  }

  // Qui est libre — seulement aujourd'hui (ne rien affirmer sur un autre jour).
  if (estAujourdhui) {
    const libres = agentsPool
      .filter((e) => e && e !== self && !parAgent.has(e))
      .map((e) => (nomAgent && nomAgent(e)) || e)
    if (libres.length === 1) phrases.push(`${libres[0]} n'a rien aujourd'hui.`)
    if (libres.length > 1) phrases.push(`${libres.slice(0, -1).join(', ')} et ${libres[libres.length - 1]} n'ont rien aujourd'hui.`)
  }

  // Quand tout va bien, LE DIRE (ajout Dimitri) — plutôt qu'un bloc muet.
  if (manques.length === 0 && estAujourdhui) {
    phrases.push("Tout est couvert aujourd'hui.")
  }
  return phrases
}
